/**
 * Reusable Confirmation Modal Component
 * 
 * A flexible modal system for confirming destructive or important actions
 * with customizable content, styling, and behavior.
 */

import { memo, useMemo } from 'react'
import ModalPortal from './ModalPortal.jsx'

// Variant style mappings for DRY principle
const VARIANT_STYLES = {
  danger: { btn: 'btn-danger', alert: 'alert-danger border-danger' },
  warning: { btn: 'btn-warning', alert: 'alert-warning border-warning' },
  primary: { btn: 'btn-primary', alert: 'alert-primary border-primary' },
  success: { btn: 'btn-success', alert: 'alert-success border-success' },
}

const ConfirmationModal = memo(({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  details,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'danger',
  icon = 'bi-exclamation-triangle-fill',
  iconColor = 'text-danger',
  isLoading = false,
  loadingText = 'Processing...',
  disabled = false,
  size = 'medium',
  children,
}) => {
  // Memoize style computations to prevent re-renders
  const styles = useMemo(() => ({
    confirmButtonClass: VARIANT_STYLES[confirmVariant]?.btn ?? VARIANT_STYLES.danger.btn,
    alertClass: VARIANT_STYLES[confirmVariant]?.alert ?? VARIANT_STYLES.danger.alert,
    titleColor: iconColor.replace('text-', ''),
  }), [confirmVariant, iconColor])

  return (
    <ModalPortal
      isOpen={isOpen}
      onClose={isLoading ? undefined : onClose}
      size={size}
      closeOnBackdrop={!isLoading}
      closeOnEscape={!isLoading}
    >
      <div className="p-4">
        {/* Header with Icon and Title */}
        <div className="text-center mb-4">
          {icon && (
            <div className={`${iconColor} mb-3`}>
              <i className={icon} style={{ fontSize: '3rem' }} />
            </div>
          )}
          {title && (
            <h4 className={`${styles.titleColor} mb-2`}>
              {title}
            </h4>
          )}
          {message && (
            <p className="text-muted mb-0">
              {message}
            </p>
          )}
        </div>

        {/* Details Section */}
        {details && (
          <div className={`alert ${styles.alertClass}`}>
            {typeof details === 'string' ? (
              <div className="d-flex align-items-start">
                <i className="bi bi-info-circle-fill me-2 mt-1" />
                <div>{details}</div>
              </div>
            ) : (
              details
            )}
          </div>
        )}

        {/* Custom Content */}
        {children}

        {/* Action Buttons */}
        <div className="d-flex gap-2 justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`btn ${styles.confirmButtonClass}`}
            onClick={onConfirm}
            disabled={isLoading || disabled}
          >
            {isLoading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" />
                {loadingText}
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </ModalPortal>
  )
})

ConfirmationModal.displayName = 'ConfirmationModal'

export default ConfirmationModal
